import PropTypes from 'prop-types';
import { hasAnalyticsData } from '@/analytics/metrics';

function formatMetric(value) {
    return new Intl.NumberFormat('en-US').format(Number(value) || 0);
}

function formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remSeconds = seconds % 60;
    if (minutes < 60) return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

function trendClasses(direction) {
    if (direction === 'up') return 'bg-green-50 text-green-700';
    if (direction === 'down') return 'bg-red-50 text-red-700';
    return 'bg-gray-100 text-gray-700';
}

function trendText(trend) {
    const rounded = Math.round(Math.abs(trend?.pct || 0));
    if (!trend || trend.direction === 'flat') return '0%';
    return `${trend.direction === 'up' ? '+' : '-'}${rounded}%`;
}

function AnalyticsBadge({
    label, icon, value, trend, formatter = formatMetric, numbersOnly = false, inline = false,
}) {
    if (inline) {
        return (
            <span className="tabular-nums text-xs text-gray-700">
                {!numbersOnly && `${label} `}
                {formatter(value)}
                {' '}
                <span className={trendClasses(trend?.direction).replace('bg-green-50', '').replace('bg-red-50', '').replace('bg-gray-100', '')}>
                    ({trendText(trend)})
                </span>
            </span>
        );
    }

    // Over the image: a quiet line of white figures, no boxes. Flat trends
    // are left out, and the label lives in the icon and the tooltip.
    const shownTrend = trend && trend.direction !== 'flat' && Math.round(Math.abs(trend.pct || 0)) > 0;
    return (
        <span
            className="inline-flex items-center gap-1 tabular-nums"
            title={`${label}: ${formatter(value)}${shownTrend ? ` (${trendText(trend)})` : ''}`}
        >
            <MetricIcon name={icon} />
            <span className="font-medium">{formatter(value)}</span>
            {shownTrend ? (
                <span className={trend.direction === 'up' ? 'text-[#a7f3d0]' : 'text-[#fecdd3]'}>
                    {trend.direction === 'up' ? '↑' : '↓'}
                    {Math.round(Math.abs(trend.pct || 0))}%
                </span>
            ) : null}
            {!numbersOnly && <span className="sr-only">{label}</span>}
        </span>
    );
}

const METRIC_ICONS = {
    visitors: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
    views: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
    time: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
};

function MetricIcon({ name }) {
    return (
        <svg
            className="h-[1.1em] w-[1.1em] shrink-0 opacity-80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {METRIC_ICONS[name]}
        </svg>
    );
}

MetricIcon.propTypes = {
    name: PropTypes.oneOf(Object.keys(METRIC_ICONS)).isRequired,
};

function formatDateLabel(x) {
    const datePart = String(x || '').split(' ')[0];
    const date = new Date(datePart);
    if (Number.isNaN(date.getTime())) return datePart || '';
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

function toPath(points, width, height, max) {
    if (!points || points.length === 0 || max <= 0) return '';
    return points
        .map((point, index) => {
            const value = Number(point?.y);
            if (!Number.isFinite(value)) return null;
            const x = points.length === 1 ? 0 : (index / (points.length - 1)) * width;
            const y = height - ((value / max) * height);
            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
        })
        .filter(Boolean)
        .join(' ');
}

function TooltipTrendChart({ series = [], comparisonSeries = [] }) {
    if (!Array.isArray(series) || series.length === 0) return null;

    const width = 260;
    const height = 86;
    const alignedComparison = series.map((point, index) => ({
        x: point.x,
        y: comparisonSeries[index]?.y ?? null,
    }));
    const max = Math.max(
        ...series.map((point) => Number(point?.y) || 0),
        ...alignedComparison.map((point) => Number(point?.y) || 0),
        1,
    );
    const currentPath = toPath(series, width, height, max);
    const comparisonPath = toPath(alignedComparison, width, height, max);
    const firstLabel = formatDateLabel(series[0]?.x);
    const lastLabel = formatDateLabel(series[series.length - 1]?.x);

    return (
        <div className="mt-2">
            <div className="flex items-center gap-3 text-[10px] text-gray-300 mb-1.5">
                <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Current
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Previous
                </span>
            </div>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                className="w-full h-[86px] overflow-visible rounded bg-black/20"
            >
                {[0.25, 0.5, 0.75].map((fraction) => {
                    const y = height * fraction;
                    return (
                        <line
                            key={fraction}
                            x1="0"
                            y1={y}
                            x2={width}
                            y2={y}
                            stroke="#374151"
                            strokeWidth="1"
                        />
                    );
                })}
                {comparisonPath && (
                    <path
                        d={comparisonPath}
                        fill="none"
                        stroke="#9ca3af"
                        strokeWidth="1.5"
                        strokeDasharray="3 3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                )}
                {currentPath && (
                    <path
                        d={currentPath}
                        fill="none"
                        stroke="#c4b5fd"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                )}
            </svg>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                <span>{firstLabel}</span>
                <span>{lastLabel}</span>
            </div>
        </div>
    );
}

function AnalyticsMetricStack({
    analytics,
    loading = false,
    className = '',
    rangeLabel = '30 days',
    tooltipPlacement = 'top',
    onClick = null,
    numbersOnly = false,
    inline = false,
}) {
    if (loading && !analytics) return null;

    // No recorded traffic: show nothing rather than a row of zeros.
    if (!hasAnalyticsData(analytics)) return null;

    const tooltipPositionClass = tooltipPlacement === 'bottom'
        ? 'left-0 top-full mt-2'
        : 'left-0 bottom-full mb-2';
    const isClickable = typeof onClick === 'function';

    return (
        <div className={`relative group/metrics ${className}`}>
            <div
                className={`flex items-center ${inline ? 'gap-3' : 'flex-wrap gap-x-2.5 gap-y-1 text-[11px] leading-none text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]'} ${isClickable ? 'cursor-pointer' : ''}`}
                onClick={(event) => {
                    if (!isClickable) return;
                    event.preventDefault();
                    event.stopPropagation();
                    onClick();
                }}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={(event) => {
                    if (!isClickable) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onClick();
                    }
                }}
            >
                <AnalyticsBadge
                    label="Visitors"
                    icon="visitors"
                    value={analytics.visitors}
                    trend={analytics.visitorsTrend}
                    numbersOnly={numbersOnly}
                    inline={inline}
                />
                <AnalyticsBadge
                    label="Views"
                    icon="views"
                    value={analytics.pageviews}
                    trend={analytics.pageviewsTrend}
                    numbersOnly={numbersOnly}
                    inline={inline}
                />
                <AnalyticsBadge
                    label="Avg. time"
                    icon="time"
                    value={analytics.avgTimeSeconds}
                    trend={analytics.avgTimeTrend}
                    formatter={formatDuration}
                    numbersOnly={numbersOnly}
                    inline={inline}
                />
            </div>
            {!isClickable && (
                <div className={`hidden group-hover/metrics:block absolute ${tooltipPositionClass} z-40 w-[260px] max-w-[85vw] rounded-lg border border-gray-700 bg-gray-900/95 text-white p-2.5 shadow-xl pointer-events-none`}>
                <p className="text-[11px] text-gray-200 mb-2">
                    Current {rangeLabel} compared to previous {rangeLabel}
                </p>
                <div className="space-y-1.5 text-[11px] leading-tight">
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-gray-300">Visitors</span>
                        <span className="text-white tabular-nums">
                            {formatMetric(analytics.visitors)} ({trendText(analytics.visitorsTrend)})
                        </span>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-gray-300">Views</span>
                        <span className="text-white tabular-nums">
                            {formatMetric(analytics.pageviews)} ({trendText(analytics.pageviewsTrend)})
                        </span>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-gray-300">Avg. time</span>
                        <span className="text-white tabular-nums">
                            {formatDuration(analytics.avgTimeSeconds)} ({trendText(analytics.avgTimeTrend)})
                        </span>
                    </div>
                </div>
                <TooltipTrendChart
                    series={analytics.series || []}
                    comparisonSeries={analytics.comparisonSeries || []}
                />
                </div>
            )}
        </div>
    );
}

AnalyticsBadge.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOf(['visitors', 'views', 'time']).isRequired,
    value: PropTypes.number,
    trend: PropTypes.shape({
        pct: PropTypes.number,
        direction: PropTypes.oneOf(['up', 'down', 'flat']),
    }),
    formatter: PropTypes.func,
    numbersOnly: PropTypes.bool,
    inline: PropTypes.bool,
};

AnalyticsMetricStack.propTypes = {
    analytics: PropTypes.shape({
        visitors: PropTypes.number,
        visits: PropTypes.number,
        pageviews: PropTypes.number,
        avgTimeSeconds: PropTypes.number,
        visitorsTrend: PropTypes.shape({
            pct: PropTypes.number,
            direction: PropTypes.oneOf(['up', 'down', 'flat']),
        }),
        visitsTrend: PropTypes.shape({
            pct: PropTypes.number,
            direction: PropTypes.oneOf(['up', 'down', 'flat']),
        }),
        pageviewsTrend: PropTypes.shape({
            pct: PropTypes.number,
            direction: PropTypes.oneOf(['up', 'down', 'flat']),
        }),
        avgTimeTrend: PropTypes.shape({
            pct: PropTypes.number,
            direction: PropTypes.oneOf(['up', 'down', 'flat']),
        }),
        series: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.string,
            y: PropTypes.number,
        })),
        comparisonSeries: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.string,
            y: PropTypes.number,
        })),
    }),
    className: PropTypes.string,
    rangeLabel: PropTypes.string,
    tooltipPlacement: PropTypes.oneOf(['top', 'bottom']),
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    numbersOnly: PropTypes.bool,
    inline: PropTypes.bool,
};

TooltipTrendChart.propTypes = {
    series: PropTypes.arrayOf(PropTypes.shape({
        x: PropTypes.string,
        y: PropTypes.number,
    })),
    comparisonSeries: PropTypes.arrayOf(PropTypes.shape({
        x: PropTypes.string,
        y: PropTypes.number,
    })),
};

export default AnalyticsMetricStack;
